import { updateEntityAspectQuery } from '@common/queries/common';
import { fabricAiDataAccessQuery, fabricDetailQuery, updateFabricAiDataAccessMutation } from '@common/queries/fabric';
import { EntityKind } from '@prophecy/graphql-types/enums';
import { theme } from '@prophecy/theme';
import { Button, Input, Stack, Switch, Text } from '@prophecy/ui-v3';
import { Spinner } from '@prophecy/ui-v3/Spinner';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { parseJSON } from '@prophecy/utils/string';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { useGraphQlMutation, useGraphQlQuery } from 'frontend/core/src/data/util';
import { useEffect, useState } from 'react';
import { Controller, Control } from 'react-hook-form';

import { AdminReadonlyController } from '../common/AdminReadonlyController';
import { ProviderFormProvider, useProviderForm } from '../common/ProviderFormContext';
import { Card } from '../common/styled';
import { ProphecyLibrary } from '../databricksFabrics/ProphecyLibrary';
import useIsOrchestrationFabric from '../hooks/useIsOrchestrationFabric';
import useIsUnifiedFabric from '../hooks/useIsUnifiedFabric';
import useUpdateFabric from '../hooks/useUpdateFabric';
import { StyledSpinner } from '../styles';
import { FabricTokens } from '../tokens';
import { FABRIC_TABS, FabricProviderType, FormContexts, GenericProviderFormData, SPARK_PROVIDER } from '../types';
import { parseUnifiedProviderValue } from '../utils/providerUtils';

type AiDataAccessSwitchProps = {
  checked?: boolean;
  disabled?: boolean;
  tooltip?: string;
  onChange?: (checked: boolean) => void;
};

const DBFS_PREFIX = 'dbfs:/';
const VOLUMES_PREFIX = '/Volumes/';

const DataplaneSection = ({ dataplaneURL }: { dataplaneURL?: string }) => {
  if (!dataplaneURL) {
    return null;
  }

  return (
    <Card gap={FabricTokens.Form.fieldGap}>
      <Text level='md'>Prophecy Dataplane</Text>
      <Input
        label='Dataplane URL'
        required
        value={dataplaneURL}
        disabled
        data-test-id='fabric-advanced-dataplane-url-input'
      />
    </Card>
  );
};

const validateArtifactsPath = (value?: string) => {
  if (!value) return true;
  if (value.startsWith(DBFS_PREFIX) && value.length > DBFS_PREFIX.length) return true;
  if (value.startsWith(VOLUMES_PREFIX) && value.length > VOLUMES_PREFIX.length) return true;
  return `Path must start with "${DBFS_PREFIX}" or "${VOLUMES_PREFIX}" and include a subdirectory`;
};

const ArtifactsSection = ({ control }: { control: Control<GenericProviderFormData> }) => {
  return (
    <AdminReadonlyController
      name='fabricData.volume'
      control={control}
      rules={{ validate: validateArtifactsPath }}
      render={({ field }) => (
        <Stack gap={FabricTokens.Form.fieldGap}>
          <Text level='md'>Artifacts</Text>
          <Input
            label='Path for uploading artifacts (whl/jar files for pipelines)'
            placeholder='dbfs:/path/to/directory (or) /Volumes/path/to/directory'
            {...field}
            data-test-id='databricks-fabric-artifacts-path-input'
          />
        </Stack>
      )}
    />
  );
};

const DevelopmentSettings = ({ control }: { control: Control }) => {
  const { readonly, readonlyMessage } = useProviderForm();
  return (
    <Stack gap={FabricTokens.Form.fieldGap}>
      <Text level='md'>Development Settings</Text>
      <Stack>
        <Controller
          control={control}
          name='dataSamplingDisabled'
          render={({ field }) => (
            <Switch
              {...field}
              helpText={
                <>
                  By default, Prophecy samples data during the interactive run experience to provide the best debugging
                  experience for users. An admin can disable this feature, for example, in production environments.{' '}
                  <a
                    target='_blank'
                    href='https://docs.prophecy.ai/data-engineering/development/runs/execution'
                    rel='noreferrer'
                    data-test-id='fabric-advanced-learn-more-link'>
                    Learn more
                  </a>
                </>
              }
              onChange={(value) => field.onChange(!value)}
              checked={!field.value}
              disabled={readonly}
              tooltip={readonly ? readonlyMessage : undefined}
              data-test-id='fabric-advanced-data-sampling-switch'>
              Allow for data sampling
            </Switch>
          )}
        />
      </Stack>
    </Stack>
  );
};

function AiDataAccessSwitch({ disabled, tooltip, ...switchProps }: AiDataAccessSwitchProps) {
  const { readonly, readonlyMessage } = useProviderForm();
  return (
    <Stack gap={FabricTokens.Form.fieldGap}>
      <Text level='md'>Copilot Settings</Text>
      <Stack>
        <Switch
          helpText='Allow Copilot to access data from the fabric. This allows Copilot to suggest data transformations and actions based on the data in the fabric.'
          {...switchProps}
          disabled={disabled || readonly}
          tooltip={tooltip || readonlyMessage}
          data-test-id='orchestration-advanced-allow-copilot-data-access'>
          Allow agent to access data
        </Switch>
      </Stack>
    </Stack>
  );
}

export function CopilotSettings({ fabricId }: { fabricId: string }) {
  const { readonly, readonlyMessage } = useProviderForm();
  const queryClient = useQueryClient();
  const queryKey = [EntityKind.Fabric, fabricId, 'aiDataAccess'];
  const globalConf = useGlobalVariables();

  const { data, isInitialLoading } = useGraphQlQuery(queryKey, fabricAiDataAccessQuery, undefined, { uid: fabricId });

  const isEnabled = Boolean(data?.Fabric?.aiDataAccessEnabled);

  const { mutateAsync: updateAiDataAccess, isLoading: isUpdating } = useGraphQlMutation(
    updateFabricAiDataAccessMutation,
    {
      onError: () => {
        toast.error({ content: 'Failed to update AI data access setting' });
      }
    }
  );

  let tooltip: string | undefined = readonlyMessage;

  if (isInitialLoading) {
    tooltip = 'Loading...';
  } else if (isUpdating) {
    tooltip = 'Updating...';
  } else if (!globalConf.isOrchestrationAiDataAccessEnabled) {
    tooltip = 'Agent data access setting is disabled for this cluster';
  } else {
    tooltip = undefined;
  }

  return (
    <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
      <AiDataAccessSwitch
        checked={isEnabled && globalConf.isOrchestrationAiDataAccessEnabled}
        disabled={isUpdating || isInitialLoading || !globalConf.isOrchestrationAiDataAccessEnabled || readonly}
        tooltip={tooltip}
        onChange={async (enabled) => {
          const previousData = queryClient.getQueryData(queryKey);
          queryClient.setQueryData(queryKey, (old: typeof data) => {
            if (!old?.Fabric) return old;
            return { ...old, Fabric: { ...old.Fabric, aiDataAccessEnabled: enabled } };
          });
          try {
            await updateAiDataAccess({ fabricUID: fabricId, aiDataAccessEnabled: enabled });
          } catch {
            queryClient.setQueryData(queryKey, previousData);
          }
        }}
      />
      {isUpdating ? <Spinner spinning size='s' /> : <Stack width='20px' />}
    </Stack>
  );
}

export default function AdvancedTab({
  formContexts,
  fabricId,
  disabled
}: {
  formContexts: FormContexts;
  fabricId: string;
  disabled?: boolean;
}) {
  const formContext = formContexts[FABRIC_TABS.advanced];
  const providersFormContext = formContexts[FABRIC_TABS.providers];
  const basicFormContext = formContexts[FABRIC_TABS.basic];
  const globalConf = useGlobalVariables();

  const { isLoading: isFabricLoading, data: fabricData } = useGraphQlQuery(
    [EntityKind.Fabric, fabricId],
    fabricDetailQuery,
    undefined,
    { uid: fabricId }
  );
  const { isLoading: isUpdatingCodeDeps, mutateAsync } = useGraphQlMutation(updateEntityAspectQuery);

  const codeDeps = parseJSON<{ dataSamplingDisabled?: boolean } | null>(
    fabricData?.Fabric?.CodeDepsAspect?.[0]?.AspectValue,
    {}
  );
  const { isOrchestrationFabric } = useIsOrchestrationFabric(formContexts);

  const providerV2 = providersFormContext.watch('providerV2');
  const fabricProviderTypesV2 = providersFormContext.watch('initialFabricProviderTypes');
  const provider = providerV2 ? parseUnifiedProviderValue(providerV2).provider : undefined;
  const isUnifiedFabric = useIsUnifiedFabric(formContexts);
  const isUnifiedSparkDbxFabric =
    fabricProviderTypesV2?.includes(FabricProviderType.Spark) && provider === SPARK_PROVIDER.DATABRICKS;

  const teamId = basicFormContext.watch('ownerUID');
  const dataplaneURL = providersFormContext.watch('unifiedFabricData.dataplaneURL');

  const { updateExistingFabric, isLoading: isUpdatingFabric } = useUpdateFabric(fabricId, formContexts, false);

  const [aiDataAccessEnabled, setAiDataAccessEnabled] = useState(false);

  useEffect(() => {
    setAiDataAccessEnabled(Boolean(fabricData?.Fabric?.aiDataAccessEnabled));
  }, [fabricData?.Fabric?.aiDataAccessEnabled]);

  const { mutateAsync: updateAiDataAccess, isLoading: isUpdatingAiAccess } = useGraphQlMutation(
    updateFabricAiDataAccessMutation
  );

  const handleUpdateAll = async () => {
    try {
      // Update development settings
      await mutateAsync({
        uid: fabricId,
        entityKind: EntityKind.Fabric,
        aspectKind: 'CodeDeps',
        aspectValue: JSON.stringify({
          ...codeDeps,
          dataSamplingDisabled: formContext.getValues('dataSamplingDisabled')
        })
      });

      // Update Prophecy Library if spark fabric
      if (isUnifiedSparkDbxFabric) {
        await providersFormContext.handleSubmit(
          async () => {
            await updateExistingFabric();
          },
          (_errors) => {
            // handleSubmit calls this callback if validation fails
            throw new Error('Please fix validation errors before saving');
          }
        )();
      }

      if (globalConf.isOrchestrationAiDataAccessEnabled) {
        await updateAiDataAccess({ fabricUID: fabricId, aiDataAccessEnabled });
      }

      toast.success({ content: 'Advanced settings updated successfully' });
    } catch (error) {
      toast.error({
        content: error instanceof Error ? error.message : 'Failed to update advanced settings'
      });
    }
  };

  return (
    <StyledSpinner spinning={isFabricLoading}>
      <ProviderFormProvider
        isOrchestrationFabric={isOrchestrationFabric}
        formContext={providersFormContext}
        updateFabricErrors={() => {}}
        fabricId={fabricId}
        originalFabricId={fabricId}
        teamId={teamId}>
        <Stack gap={FabricTokens.Form.sectionGap} padding={`0 0 ${theme.spaces.x32}`}>
          {isUnifiedSparkDbxFabric ? (
            <Card gap={FabricTokens.Form.fieldGap}>
              <ProphecyLibrary />
              <ArtifactsSection control={providersFormContext.control} />
              <DevelopmentSettings control={formContext.control} />
            </Card>
          ) : (
            <DevelopmentSettings control={formContext.control} />
          )}

          {/* Dataplane URL section for SQL fabrics (read-only display) */}
          {isUnifiedFabric && <DataplaneSection dataplaneURL={dataplaneURL} />}

          <AiDataAccessSwitch
            tooltip={
              !globalConf.isOrchestrationAiDataAccessEnabled
                ? 'Agent data access setting is disabled for this cluster'
                : undefined
            }
            checked={aiDataAccessEnabled && globalConf.isOrchestrationAiDataAccessEnabled}
            onChange={setAiDataAccessEnabled}
            disabled={!globalConf.isOrchestrationAiDataAccessEnabled}
          />

          <Stack direction='horizontal' align='end' gap={theme.spaces.x12}>
            <Button
              loading={isUpdatingCodeDeps || isUpdatingFabric || isUpdatingAiAccess}
              disableWhileLoading
              disabled={disabled}
              tooltip={disabled ? 'Only team admins can update fabric.' : undefined}
              variant='primary'
              size='m'
              onClick={handleUpdateAll}
              data-test-id='fabric-advanced-update-button'>
              Update
            </Button>
          </Stack>
        </Stack>
      </ProviderFormProvider>
    </StyledSpinner>
  );
}
